<script setup lang="ts">
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LinkTextPrimary } from '@shared/ui/links'

import { useTableDisciplineTasks } from '../model/table-discipline-tasks.hook'
import DisciplineTaskStatus from './DisciplineTaskStatus.vue'
import DisciplineTaskActions from './DisciplineTaskActions.vue'

defineOptions({
  name: 'TableDisciplineTasks',
})

const COLUMNS: readonly { title: string, className: string }[] = [
  { title: '#', className: 'table-discipline-tasks__id' },
  { title: 'Discipline', className: '' },
  { title: 'Tutor', className: '' },
  { title: 'Status', className: '' },
  { title: 'Type', className: '' },
  { title: 'Due date & time', className: 'table-discipline-tasks__date' },
  { title: '', className: 'table-discipline-tasks__actions' },
]

const {
  rows,
  isLoading,
  hasTasks,
  meta,
  perPageOptions,
  openTask,
  setChangePage,
  setChangePerPage,
} = useTableDisciplineTasks()
</script>

<template>
  <div class="table-discipline-tasks">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh
            v-for="column in COLUMNS"
            :key="column.title"
            :class="column.className"
          >
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              {{ column.title }}
            </AppText>
          </AppTh>
        </AppTr>
      </template>

      <template
        v-if="hasTasks"
        #body
      >
        <AppTr
          v-for="row in rows"
          :key="row.task.id"
        >
          <AppTd class="medium table-discipline-tasks__id">
            <button
              type="button"
              class="table-discipline-tasks__open"
              @click="openTask(row.task)"
            >
              <AppText
                name="body1-medium"
                color="blue-600"
              >
                {{ row.task.id }}
              </AppText>
            </button>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="row.disciplineUrl"
              :href="row.disciplineUrl"
            >
              {{ row.disciplineName }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
            >
              {{ row.disciplineName }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="row.tutorUrl"
              :href="row.tutorUrl"
            >
              {{ row.tutorName }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
              color="red-600"
            >
              Unassigned tutor
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <DisciplineTaskStatus :task="row.task" />
          </AppTd>
          <AppTd class="medium">
            <AppText name="body1">
              {{ row.typeLabel }}
            </AppText>
          </AppTd>
          <AppTd class="medium table-discipline-tasks__date">
            <AppText
              name="body1"
              :color="row.isOverdue ? 'red-600' : 'slate-900'"
            >
              {{ row.dueDate }}
            </AppText>
          </AppTd>
          <AppTd class="zero-padding table-discipline-tasks__actions">
            <DisciplineTaskActions :task="row.task" />
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableDisciplineTasks.scss" />
